import React, { useContext } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { AuthContext } from "../../context/AuthContextProvider";
import { RequireAuth } from "../../RequireAuth";
import { ChartsTooltip } from "@mui/x-charts";

/* Shared tooltip styling for the donut charts */
const DONUT_TOOLTIP_SX = {
  backgroundColor: "var(--app-surface)",
  borderRadius: "10px",
  // boxShadow: "0px 4px 14px rgba(0,0,0,0.15)",
  padding: "10px 14px",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--app-text)",
  minWidth: "140px",
  whiteSpace: "pre-line",
  "& .MuiChartsTooltip-paper": {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: 0,
    border: "none",
  },
  "& .MuiChartsTooltip-mark": { display: "none" },
  "& .MuiChartsTooltip-labelCell": { display: "none" },
  "& .MuiChartsTooltip-valueCell": { paddingLeft: "0px !important" },
};

/* Card surface shared by every donut panel. Exported so donuts placed
   individually on the dashboard keep the same surface as the grouped section. */
export const DONUT_PANEL_SX = {
  backgroundColor: "var(--app-surface)",
  border: "1px solid var(--app-border)",
  borderRadius: "16px",
  p: { xs: 2, md: 2.5 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  opacity: 0,
  animation: "dashFadeUp .5s ease forwards",
  transition: " .22s ease, border-color .22s ease",
  "&:hover": {
    borderColor: "rgba(87,81,233,0.35)",
  },
};

/* Donut geometry, shared so separately-placed donuts stay identical in size. */
export const useDonutSizing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return {
    isMobile,
    chartSize: isMobile ? 190 : 227,
    outerR: isMobile ? 80 : 100,
    innerR: isMobile ? 50 : 60,
  };
};

/* Donut datasets. Exported so the dashboard can place each donut in its own
   row without duplicating the shaping logic. */
export const buildTestResultsData = (analyticsData, INTERPRETATION_COLORS = {}) =>
  Object.entries(analyticsData?.interpretationStatusCounts || {}).map(
    ([label, value]) => {
      const formatted = label
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return {
        id: formatted,
        label: formatted,
        value,
        color: INTERPRETATION_COLORS[formatted] || "#CCCCCC",
      };
    },
  );

export const buildPatientsData = (analyticsData) => [
  {
    id: "New",
    label: "New Patients",
    value: analyticsData?.patientClassification?.newPatients ?? 0,
    color: "#5751E9",
  },
  {
    id: "Returning",
    label: "Returning Patients",
    value: analyticsData?.patientClassification?.returning ?? 0,
    color: "#C2C9FF",
  },
];

export const buildGenderData = (analyticsData) => {
  const gender = analyticsData?.genderClassification || {};
  return [
    { id: "Male", label: "Male", value: gender.male || 0, color: "#5751E9" },
    { id: "Female", label: "Female", value: gender.female || 0, color: "#FB8588" },
    { id: "Other", label: "Other", value: gender.other || 0, color: "#C2C9FF" },
  ];
};

/**
 * Reusable donut card.
 * - Legend rendered BELOW the chart (wraps, never clips like the built-in legend).
 * - Flat arcs, rounded and separated by a gap rather than a shadow.
 */
export const DonutCard = ({
  title,
  accent,
  cardSx,
  data,
  emptyText,
  pieChartloading,
  isMobile,
  chartSize,
  innerR,
  outerR,
  headerExtra = null,
}) => {
  const series = (data || []).filter((d) => d.value > 0);
  const total = series.reduce((s, d) => s + d.value, 0);

  return (
    <Box sx={cardSx}>
      {/* Header with accent bar */}
      <Box
        sx={{
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 18,
            borderRadius: "4px",
            backgroundColor: accent,
          }}
        />
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "var(--app-text)", fontSize: "18px" }}
        >
          {title}
        </Typography>
        {headerExtra}
      </Box>

      {/* Donut */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mt: 1.5,
        }}
      >
        {total > 0 ? (
          <Box
            sx={{
              position: "relative",
              width: chartSize,
              height: chartSize,
              mx: "auto",
            }}
          >
            <PieChart
              width={chartSize}
              height={chartSize}
              series={[
                {
                  data: series,
                  innerRadius: innerR,
                  outerRadius: outerR,
                  paddingAngle: 2,
                  cornerRadius: 6,
                  valueFormatter: (v) => `${v.label}:\n${v.value}`,
                  // Hover animation: the hovered slice grows, the rest fade —
                  // matches the donut hover behaviour on the Analytics page.
                  highlightScope: { highlight: "item", fade: "global" },
                  highlighted: { additionalRadius: 6 },
                  faded: { additionalRadius: -4, color: "var(--app-border)" },
                },
              ]}
              colors={series.map((d) => d.color)}
              slots={{ tooltip: ChartsTooltip }}
              slotProps={{
                legend: { hidden: true },
                tooltip: {
                  trigger: "item",
                  labelFormatter: () => "",
                  sx: DONUT_TOOLTIP_SX,
                },
              }}
              sx={{
                "& .MuiPieArc-root, & .MuiChartsArc-root": {
                  stroke: "none",
                },
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{ fontWeight: 600, fontSize: 17, color: "var(--app-text)" }}
              >
                Total
              </Typography>
              <Typography
                sx={{ fontSize: 26, fontWeight: 900, color: "var(--app-text)" }}
              >
                {total}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: chartSize,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={pieChartloading} alt="No data" width={120} height={120} />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "var(--app-text-muted)",
                mt: 1,
                fontFamily: "Figtree",
              }}
            >
              {emptyText}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Legend (below the donut, wraps cleanly) */}
      {total > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px 18px",
            mt: 2,
          }}
        >
          {series.map((d) => (
            <Box
              key={d.id}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: d.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: "var(--app-text)",
                  fontWeight: 500,
                }}
              >
                {d.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: "var(--app-text)",
                  fontWeight: 800,
                }}
              >
                {d.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const DashboardPieSection = ({
  analyticsData,
  INTERPRETATION_COLORS,
  pieChartloading,
  pieSeriesData,
}) => {
  const { user } = useContext(AuthContext);
  const { isMobile, chartSize, innerR, outerR } = useDonutSizing();

  // All three donut cards share the same surface
  const cardPlain = DONUT_PANEL_SX;

  // Donut datasets (values + project colours)
  const testResultsData = buildTestResultsData(
    analyticsData,
    INTERPRETATION_COLORS,
  );
  const patientsData = buildPatientsData(analyticsData);
  const genderData = buildGenderData(analyticsData);

  const donutCommon = {
    pieChartloading,
    isMobile,
    chartSize,
    innerR,
    outerR,
  };

  return (
    <>
      {user?.userType === "b2b-doctor" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            width: "100%",
            gap: { xs: 2, md: 2.5 },
          }}
        >
          <RequireAuth role={["b2b-doctor"]} permission={"canInterpreteReport"}>
            <DonutCard
              title="Interpretation Results"
              accent="#5751E9"
              cardSx={cardPlain}
              data={testResultsData}
              emptyText="No Data Available"
              {...donutCommon}
            />
          </RequireAuth>

          <RequireAuth role={["b2b-doctor"]} permission={"canViewCases"}>
            <DonutCard
              title="Referred Cases"
              accent="#5751E9"
              cardSx={cardPlain}
              data={pieSeriesData || []}
              emptyText="No Referral Cases"
              {...donutCommon}
            />
          </RequireAuth>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            width: "100%",
            gap: { xs: 2, md: 2.5 },
          }}
        >
          <DonutCard
            title="Test Results"
            accent="#5751E9"
            cardSx={cardPlain}
            data={testResultsData}
            emptyText="No Data Available"
            {...donutCommon}
          />
          <DonutCard
            title="Patients"
            accent="#5751E9"
            cardSx={cardPlain}
            data={patientsData}
            emptyText="No Patients"
            {...donutCommon}
          />
          <DonutCard
            title="Tests by Gender"
            accent="#5751E9"
            cardSx={cardPlain}
            data={genderData}
            emptyText="No Gender Data"
            {...donutCommon}
          />
        </Box>
      )}
    </>
  );
};
export default DashboardPieSection;
